import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Mail, MessageSquare, RefreshCw, CheckCircle, XCircle,
  Search, Filter, Calendar, AlertTriangle
} from 'lucide-react';

const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

export default function CommsLogs() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  
  // Filters
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadLogs();
  }, [user, navigate]);

  useEffect(() => {
    filterLogs();
  }, [logs, channelFilter, statusFilter, searchQuery]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (channelFilter !== 'all') params.append('channel', channelFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const res = await axios.get(`${API}/platform/email/logs?${params.toString()}`, getAuthConfig());
      setLogs(res.data?.logs || []);
    } catch (error) {
      console.error('Failed to load logs:', error);
      toast.error('Failed to load delivery logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = [...logs];
    
    if (channelFilter !== 'all') {
      filtered = filtered.filter(log => getChannel(log) === channelFilter);
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(log => log.status === statusFilter);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log => 
        getRecipient(log)?.toLowerCase().includes(query) ||
        getNotificationName(log)?.toLowerCase().includes(query) ||
        log.error_message?.toLowerCase().includes(query) ||
        log.error?.toLowerCase().includes(query)
      );
    }
    
    setFilteredLogs(filtered);
  };

  const formatDate = (log) => {
    const dateStr = log.timestamp || log.created_at;
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getNotificationName = (log) => {
    // Handle broadcast summary logs specially
    if (log.type === 'broadcast_summary') {
      return `📢 Broadcast: ${log.trigger_id || 'notification'}`;
    }
    // Remove broadcast_ prefix if present and format nicely
    let name = log.notification || log.trigger_id || log.type || '-';
    if (name.startsWith('broadcast_')) {
      name = `📢 ${name.replace('broadcast_', '')}`;
    }
    // Convert underscores to spaces and capitalize
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getRecipient = (log) => {
    // For broadcast summary, show the recipient count
    if (log.type === 'broadcast_summary' && log.metadata?.total_recipients) {
      return `${log.metadata.total_recipients} recipients`;
    }
    return log.recipient || log.to_email || '-';
  };

  const getChannel = (log) => {
    // Determine channel from log data
    if (log.channel) return log.channel;
    if (log.twilio_sid) return 'sms';
    if (log.to_email || log.template_id) return 'email';
    return 'email'; // default
  };

  const getStatusBadge = (status) => {
    if (status === 'sent' || status === 'delivered' || status === 'completed') {
      return (
        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">
          <CheckCircle className="w-3 h-3 mr-1" />
          {status === 'completed' ? 'Completed' : 'Sent'}
        </Badge>
      );
    }
    if (status === 'failed' || status === 'error') {
      return (
        <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px]">
          <XCircle className="w-3 h-3 mr-1" />
          Failed
        </Badge>
      );
    }
    if (status === 'skipped_quiet_hours') {
      return (
        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px]">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Quiet Hours
        </Badge>
      );
    }
    if (status === 'skipped_opted_out') {
      return (
        <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30 text-[10px]">
          <XCircle className="w-3 h-3 mr-1" />
          Opted Out
        </Badge>
      );
    }
    return (
      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">
        <AlertTriangle className="w-3 h-3 mr-1" />
        {status || 'Unknown'}
      </Badge>
    );
  };

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout backLink="/super-admin/comms">
      <div className="space-y-6" data-testid="comms-logs">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Delivery Logs</h1>
            <p className="text-sm text-zinc-500">Track notification delivery status</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadLogs}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    placeholder="Search recipient, notification..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-zinc-800 border-zinc-700 text-zinc-100"
                  />
                </div>
              </div>
              
              {/* Channel Filter */}
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700 text-zinc-100">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Channel" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All Channels</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700 text-zinc-100">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading logs...
          </div>
        ) : filteredLogs.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
              <p className="text-zinc-400">No delivery logs found</p>
              <p className="text-sm text-zinc-500 mt-1">
                {logs.length === 0 
                  ? 'Logs will appear here when notifications are sent'
                  : 'Try adjusting your filters'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider p-4">
                      Time
                    </th>
                    <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider p-4">
                      Notification
                    </th>
                    <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider p-4">
                      Channel
                    </th>
                    <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider p-4">
                      Recipient
                    </th>
                    <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider p-4">
                      Status
                    </th>
                    <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider p-4">
                      Error
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log, idx) => (
                    <tr key={log.id || idx} className="border-b border-zinc-800 last:border-0 hover:bg-zinc-800/50">
                      <td className="p-4 text-sm text-zinc-400 whitespace-nowrap">
                        {formatDate(log)}
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-zinc-100">
                          {getNotificationName(log)}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {getChannel(log) === 'email' ? (
                            <Mail className="w-4 h-4 text-blue-400" />
                          ) : (
                            <MessageSquare className="w-4 h-4 text-emerald-400" />
                          )}
                          <span className="text-sm text-zinc-400 capitalize">
                            {getChannel(log)}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-zinc-300 font-mono">
                          {getRecipient(log)}
                        </span>
                      </td>
                      <td className="p-4">
                        {getStatusBadge(log.status)}
                      </td>
                      <td className="p-4">
                        {(log.error_message || log.error) ? (
                          <span className="text-sm text-red-400 truncate max-w-xs block">
                            {log.error_message || log.error}
                          </span>
                        ) : (
                          <span className="text-zinc-600">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Stats Summary */}
        {logs.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-zinc-100">{logs.length}</p>
                <p className="text-xs text-zinc-500">Total Messages</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-emerald-400">
                  {logs.filter(l => l.status === 'sent' || l.status === 'delivered').length}
                </p>
                <p className="text-xs text-zinc-500">Delivered</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-400">
                  {logs.filter(l => l.status === 'failed' || l.status === 'error').length}
                </p>
                <p className="text-xs text-zinc-500">Failed</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
}
